import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Product } from '@/types'

import Link from 'next/link'
import Image from 'next/image'
import { useEffect, useMemo, useState } from 'react'
import { ArrowRight, Check, X } from 'lucide-react'
import { formatPrice } from '@/lib/format'
import { getProducts } from '@/lib/products'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { useCart } from '@/components/generals/cart-provider'
import { primaryButton } from '@/components/generals/button-styles'
import QuantityStepper from '@/components/shop/quantity-stepper'
import OrderForm from '@/components/shop/order-form'
import { PRODUCT_FALLBACK_IMAGE } from '@/components/shop/product-card'

interface CartPageProps {
    products: Array<Product>
    failed: boolean
}

interface Confirmation {
    reference: string
    total: number
    email: string
}

const STEPS = [
    'Send your order request below. Nothing is charged yet.',
    'Within one working day we email an invoice with tracked shipping to your country.',
    'Pay by card from the invoice and we post your parcel within three days.'
]

const CartPage: FC<CartPageProps> = ({ products, failed }): ReactNode => {
    const { lines, ready, setQuantity, removeItem, clear } = useCart()
    const [confirmation, setConfirmation] = useState<Confirmation | null>(null)

    const items = useMemo(
        () => lines
            .map(line => ({ line, product: products.find(product => product.id === line.productId) }))
            .filter((entry): entry is { line: typeof entry.line; product: Product } => Boolean(entry.product)),
        [lines, products]
    )

    // Drop anything the shop no longer sells, so the cart never offers a missing product
    useEffect(() => {
        if (!ready || failed) return
        lines.forEach(line => {
            if (!products.some(product => product.id === line.productId)) removeItem(line.productId)
        })
    }, [ready, failed, lines, products, removeItem])

    const subtotal = items.reduce((sum, { line, product }) => sum + product.price * line.quantity, 0)
    const soldOut = items.filter(({ product }) => !product.available)

    const handleSuccess = (result: Confirmation): void => {
        setConfirmation(result)
        clear()
        window.scrollTo({ top: 0 })
    }

    return (
        <Layout>
            <Seo title='Your cart' description="Review your handmade paper and send an order request to Qog'oz Atelier in Samarkand." />

            <section id='cart' aria-labelledby='cart-title' className='container-page pb-24 pt-12 md:pb-32 md:pt-20'>
                <p className='flex items-center gap-4 text-sm font-medium text-bark-soft'>
                    <span aria-hidden='true' className='h-px w-10 bg-pomegranate' />
                    Your cart
                </p>

                {confirmation ? (
                    <div className='mt-8 max-w-2xl animate-in fade-in slide-in-from-bottom-3 duration-700'>
                        <span className='inline-flex size-14 items-center justify-center rounded-full bg-bark text-paper'>
                            <Check className='size-6' strokeWidth={1.75} />
                        </span>
                        <h1 id='cart-title' className='mt-8 font-heading text-[clamp(2.4rem,5vw,4rem)] font-light leading-[1.02] text-bark'>
                            Thank you. Your order is with us.
                        </h1>
                        <p className='mt-6 text-[1.08rem] leading-relaxed text-bark-soft'>
                            Your reference is <strong className='font-semibold text-bark'>{confirmation.reference}</strong> for {formatPrice(confirmation.total)} before shipping. Within one working day we will email an invoice with tracked shipping to <strong className='font-semibold text-bark'>{confirmation.email}</strong>.
                        </p>
                        <div className='mt-10 flex flex-wrap gap-3'>
                            <Link href='/shop' className={primaryButton}>
                                Keep browsing
                                <ArrowRight className='size-4' strokeWidth={1.75} />
                            </Link>
                        </div>
                    </div>
                ) : (
                    <>
                        <h1 id='cart-title' className='mt-6 font-heading text-[clamp(2.6rem,6vw,5rem)] font-light leading-[0.98] tracking-[-0.025em] text-bark'>
                            Ready to wrap.
                        </h1>

                        {!ready ? (
                            <div aria-busy='true' className='mt-14 space-y-6'>
                                {[0, 1].map(index => (
                                    <div key={index} className='h-28 rounded-[3px] bg-paper-deep' />
                                ))}
                            </div>
                        ) : items.length === 0 ? (
                            <div className='mt-14 max-w-xl'>
                                <p className='font-heading text-[1.6rem] leading-snug text-bark'>
                                    Your cart is empty, like a fresh sheet waiting for ink.
                                </p>
                                <p className='mt-3 text-bark-soft'>
                                    {failed ? 'The shop could not be reached just now. Please refresh in a moment.' : 'Choose some sheets, a notebook or a wedding set to begin.'}
                                </p>
                                <Link href='/shop' className={`${primaryButton} mt-8`}>
                                    Go to the shop
                                    <ArrowRight className='size-4' strokeWidth={1.75} />
                                </Link>
                            </div>
                        ) : (
                            <div className='mt-14 grid gap-14 lg:grid-cols-12 lg:gap-10'>
                                <div className='lg:col-span-7'>
                                    <ul className='divide-y divide-bark/12 border-y border-bark/12'>
                                        {items.map(({ line, product }) => (
                                            <li key={product.id} className='flex gap-5 py-6 sm:gap-7'>
                                                <Link href={`/shop/${product.slug}`} className='paper-lift block w-24 shrink-0 sm:w-28' tabIndex={-1} aria-hidden='true'>
                                                    <div className='deckle relative aspect-[4/5] overflow-hidden bg-paper-deep'>
                                                        <Image
                                                            src={product.image || PRODUCT_FALLBACK_IMAGE}
                                                            alt=''
                                                            fill
                                                            sizes='112px'
                                                            className='object-cover'
                                                        />
                                                    </div>
                                                </Link>

                                                <div className='flex min-w-0 flex-1 flex-col'>
                                                    <div className='flex items-start justify-between gap-4'>
                                                        <div className='min-w-0'>
                                                            <h2 className='font-heading text-[1.3rem] leading-tight text-bark'>
                                                                <Link href={`/shop/${product.slug}`} className='hover:text-pomegranate'>
                                                                    <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                                                                </Link>
                                                            </h2>
                                                            {product.packLabel && (
                                                                <p className='mt-1 text-sm text-bark-soft'>
                                                                    <span data-mdlfy-cms={`products:${product.id}:pack_label`}>{product.packLabel}</span>
                                                                </p>
                                                            )}
                                                            {!product.available && (
                                                                <p className='mt-2 text-sm font-medium text-pomegranate'>Sold out just now, please remove it to order.</p>
                                                            )}
                                                        </div>
                                                        <p className='shrink-0 font-heading text-[1.2rem] text-pomegranate'>
                                                            {formatPrice(product.price * line.quantity)}
                                                        </p>
                                                    </div>

                                                    <div className='mt-auto flex flex-wrap items-center justify-between gap-3 pt-4'>
                                                        <QuantityStepper
                                                            size='sm'
                                                            value={line.quantity}
                                                            onChange={value => setQuantity(product.id, value)}
                                                            label={`Quantity of ${product.name}`}
                                                        />
                                                        <button
                                                            type='button'
                                                            onClick={() => removeItem(product.id)}
                                                            className='inline-flex items-center gap-1.5 rounded-sm px-2 py-2 text-sm text-bark-soft transition-colors hover:text-pomegranate focus-visible:outline-2 focus-visible:outline-bark'
                                                        >
                                                            <X className='size-4' strokeWidth={1.75} />
                                                            Remove<span className='sr-only'> {product.name}</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>

                                    <div className='mt-8 flex items-baseline justify-between gap-6'>
                                        <p className='text-bark-soft'>Subtotal, before shipping</p>
                                        <p className='font-heading text-[2rem] font-light text-pomegranate'>{formatPrice(subtotal)}</p>
                                    </div>

                                    <div className='mt-10 rounded-[3px] bg-paper-deep/60 p-6 sm:p-8'>
                                        <h2 className='text-sm font-medium text-bark-soft'>How ordering works</h2>
                                        <ol className='mt-4 space-y-4'>
                                            {STEPS.map((step, index) => (
                                                <li key={step} className='flex gap-4 text-[0.98rem] leading-relaxed text-bark'>
                                                    <span className='font-semibold tabular-nums text-pomegranate'>{index + 1}</span>
                                                    {step}
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                </div>

                                <aside aria-labelledby='order-title' className='lg:col-span-5'>
                                    <div className='lg:sticky lg:top-28'>
                                        <div className='rounded-[3px] border border-bark/15 bg-paper p-6 sm:p-8'>
                                            <h2 id='order-title' className='font-heading text-[1.8rem] font-light text-bark'>
                                                Send your order request
                                            </h2>
                                            <p className='mt-2 mb-7 text-[0.98rem] leading-relaxed text-bark-soft'>
                                                No payment now. We confirm every order by email first.
                                            </p>
                                            {soldOut.length > 0 ? (
                                                <p role='alert' className='text-sm text-pomegranate'>
                                                    Please remove the sold out items before sending your order.
                                                </p>
                                            ) : (
                                                <OrderForm lines={items.map(({ line }) => line)} onSuccess={handleSuccess} />
                                            )}
                                        </div>
                                    </div>
                                </aside>
                            </div>
                        )}
                    </>
                )}
            </section>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<CartPageProps> = async () => {
    try {
        const products = await getProducts()
        return { props: { products, failed: false } }
    } catch (error) {
        console.error('Products could not be loaded for the cart', error instanceof Error ? error.message : error)
        return { props: { products: [], failed: true } }
    }
}

export default CartPage
